import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, Send, Loader2, Copy, Check, Lock,
  Sparkles, CornerDownLeft, MessageSquare, Crown, ChevronRight, LogIn
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const FormatMessage = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');
  
  return lines.map((line, idx) => {
    let html = line
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="bg-white/10 text-emerald-300 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');
    
    if (line.trim().startsWith('---')) {
      return <hr key={idx} className="border-white/10 my-3" />;
    }
    const h3Match = line.match(/^###\s+(.+)/);
    if (h3Match) {
      return <p key={idx} className="text-sm font-bold text-white mt-3 mb-1" dangerouslySetInnerHTML={{ __html: h3Match[1].replace(/\*\*(.*?)\*\*/g, '$1') }} />;
    }
    const h2Match = line.match(/^##\s+(.+)/);
    if (h2Match) {
      return <p key={idx} className="text-base font-bold text-white mt-3 mb-1" dangerouslySetInnerHTML={{ __html: h2Match[1].replace(/\*\*(.*?)\*\*/g, '$1') }} />;
    }
    if (line.trim().startsWith('>')) {
      return (
        <div key={idx} className="border-l-2 border-emerald-500/40 pl-3 my-1.5 italic text-slate-400">
          <span dangerouslySetInnerHTML={{ __html: html.replace(/^>\s*/, '') }} />
        </div>
      );
    }
    if (line.trim().match(/^[•\-]\s/)) {
      return (
        <div key={idx} className="flex gap-2.5 my-1 pl-1">
          <span className="text-emerald-400 mt-0.5 text-xs">&#9679;</span>
          <span className="flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: html.replace(/^[•\-]\s*/, '') }} />
        </div>
      );
    }
    const numMatch = line.match(/^(\d+)\.\s/);
    if (numMatch) {
      return (
        <div key={idx} className="flex gap-2.5 my-1 pl-1">
          <span className="text-emerald-400 font-semibold text-xs min-w-[1.2rem] text-right mt-0.5">{numMatch[1]}.</span>
          <span className="flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: html.replace(/^\d+\.\s*/, '') }} />
        </div>
      );
    }
    if (!line.trim()) return <div key={idx} className="h-2.5" />;
    return <div key={idx} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  });
};

export const FreeChatPanel = ({ onClose, guestMode = false }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const isPremium = !guestMode && user?.subscription_status === 'active';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageInfo, setUsageInfo] = useState({ used: 0, limit: guestMode ? 10 : (isPremium ? 200 : 10), remaining: guestMode ? 10 : (isPremium ? 200 : 10) });
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [inputRows, setInputRows] = useState(1);
  const [isBlocked, setIsBlocked] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getGuestId = useCallback(() => {
    let gid = localStorage.getItem('po_guest_id');
    if (!gid) {
      gid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('po_guest_id', gid);
    }
    return gid;
  }, []);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setMessages([{ 
      role: 'assistant', 
      content: `Cześć. O czym chcesz porozmawiać?`,
      ts: Date.now() 
    }]);
    if (guestMode) {
      const gid = getGuestId();
      axios.get(`${API_BASE}/api/ai-chat/guest/usage?guest_id=${gid}`).then(res => {
        setUsageInfo(res.data);
        if (res.data.remaining <= 0) setIsBlocked(true);
      }).catch(() => {});
    } else {
      api.get('/ai-chat/usage').then(res => {
        setUsageInfo(res.data);
        if (res.data.remaining <= 0) setIsBlocked(true);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const copyMessage = async (content, index) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch(e) { /* ignore */ }
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || isLoading || isBlocked) return;
    setInput('');
    setInputRows(1);
    setError(null);

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const history = messages
        .slice(-12)
        .map(m => ({ role: m.role, content: m.content }));

      let res;
      const makeRequest = async () => {
        if (guestMode) {
          return await axios.post(`${API_BASE}/api/ai-chat/guest`, {
            message: msg,
            conversation_history: history,
            guest_id: getGuestId()
          }, { timeout: 90000 });
        } else {
          return await api.post('/ai-chat', {
            message: msg,
            conversation_history: history
          });
        }
      };

      try {
        res = await makeRequest();
      } catch (firstErr) {
        if (firstErr.response) throw firstErr;
        res = await makeRequest();
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        ts: Date.now()
      }]);
      setUsageInfo({ used: res.data.used, limit: res.data.limit, remaining: res.data.remaining });
      if (res.data.remaining <= 0) setIsBlocked(true);
    } catch (e) {
      if (e.response?.status === 429) {
        setIsBlocked(true);
        setError(null);
      } else if (e.response?.status === 403) {
        setIsBlocked(true);
        setError(null);
      } else {
        setError('Błąd połączenia. Spróbuj ponownie.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    const lines = e.target.value.split('\n').length;
    setInputRows(Math.min(Math.max(lines, 1), 4));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="free-chat-panel">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-lg" onClick={onClose} />
      
      <div 
        className="relative w-full sm:w-[640px] sm:max-w-[95vw] h-[92vh] sm:h-[85vh] sm:max-h-[800px] flex flex-col overflow-hidden"
        style={{ 
          background: '#0a1628',
          borderRadius: '20px 20px 0 0',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 -10px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 relative" style={{ background: 'linear-gradient(180deg, rgba(16,185,129,0.08) 0%, transparent 100%)' }}>
          <div className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0a1628]" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-base tracking-tight" data-testid="chat-title">Swobodna rozmowa</h3>
                  <p className="text-[11px] text-slate-500 font-medium">
                    PromptOrigin
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5" data-testid="chat-usage-counter">
                  <Sparkles className={`w-3 h-3 ${isPremium ? 'text-emerald-400' : 'text-amber-400'}`} />
                  <span className="text-[11px] font-semibold text-slate-400">{usageInfo.remaining}/{usageInfo.limit}</span>
                </div>
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="close-chat-btn">
                  <X className="w-5 h-5 text-slate-500 hover:text-white transition-colors" />
                </button>
              </div>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        {/* MESSAGES */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5" data-testid="chat-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-1" 
                  style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div className={`max-w-[88%] sm:max-w-[82%] ${msg.role === 'user' ? 'ml-auto' : ''}`}>
                <div className={`rounded-2xl px-4 py-3 ${
                  msg.role === 'assistant'
                    ? 'bg-white/[0.05] border border-white/[0.06]'
                    : 'text-white'
                }`} style={msg.role === 'user' ? { background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 2px 12px rgba(16,185,129,0.25)' } : {}}>
                  <div className={`text-[13px] ${msg.role === 'assistant' ? 'text-slate-300' : 'text-white'}`}>
                    <FormatMessage content={msg.content} />
                  </div>
                </div>
                
                {msg.role === 'assistant' && idx > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 ml-1">
                    <button
                      onClick={() => copyMessage(msg.content, idx)}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-slate-600 hover:text-slate-400 transition-colors"
                      data-testid={`chat-copy-${idx}`}
                    >
                      {copiedIndex === idx ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" 
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              </div>
              <div className="bg-white/[0.05] border border-white/[0.06] rounded-2xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] text-slate-500">Myślę</span>
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* ERROR */}
        {error && (
          <div className="flex-shrink-0 mx-4 mb-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/15">
            <p className="text-xs text-red-400 font-medium">{error}</p>
          </div>
        )}

        {/* BLOCKED STATE — paywall for free users */}
        {isBlocked && !isPremium && (
          <div className="flex-shrink-0 mx-4 mb-3" data-testid="chat-paywall">
            <div 
              className="rounded-xl px-5 py-5 text-center"
              style={{
                background: guestMode
                  ? 'linear-gradient(135deg, rgba(14,165,233,0.1), rgba(16,185,129,0.06))'
                  : 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(16,185,129,0.06))',
                border: guestMode
                  ? '1px solid rgba(14,165,233,0.18)'
                  : '1px solid rgba(251,191,36,0.18)',
              }}
            >
              {guestMode ? (
                <>
                  <LogIn className="w-6 h-6 text-sky-400 mx-auto mb-2.5" />
                  <p className="text-sm font-bold text-white mb-1">Chcesz więcej?</p>
                  <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                    Zaloguj się, żeby odblokować więcej wiadomości i pełny dostęp do platformy.
                  </p>
                  <button
                    onClick={() => { onClose(); navigate('/auth'); }}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #0ea5e9, #10b981)',
                      boxShadow: '0 4px 16px rgba(14,165,233,0.3)',
                    }}
                    data-testid="chat-login-btn"
                  >
                    Zaloguj się
                    <ChevronRight className="w-4 h-4 inline ml-1" />
                  </button>
                </>
              ) : (
                <>
                  <Crown className="w-6 h-6 text-amber-400 mx-auto mb-2.5" />
                  <p className="text-sm font-bold text-white mb-1">Kontynuuj bez ograniczeń</p>
                  <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                    W planie Premium swobodna rozmowa jest dostępna bez dziennego limitu.
                  </p>
                  <button
                    onClick={() => { onClose(); window.location.href = '/paywall'; }}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      boxShadow: '0 4px 16px rgba(245,158,11,0.3)',
                    }}
                    data-testid="chat-upgrade-btn"
                  >
                    Sprawdź plany Premium
                    <ChevronRight className="w-4 h-4 inline ml-1" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* BLOCKED STATE — limit for premium users */}
        {isBlocked && isPremium && (
          <div className="flex-shrink-0 mx-4 mb-3">
            <div className="rounded-xl px-4 py-3 bg-white/[0.04] border border-white/[0.06] text-center">
              <p className="text-xs text-slate-400">Dzienny limit wiadomości wykorzystany. Wróć jutro!</p>
            </div>
          </div>
        )}

        {/* INPUT */}
        <div className="flex-shrink-0 p-4 border-t border-white/[0.06]" style={{ background: 'rgba(10,22,40,0.95)' }}>
          <div className="flex gap-2.5 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isBlocked ? (isPremium ? "Limit wyczerpany" : "Odblokuj pełny dostęp...") : "Napisz cokolwiek..."}
                rows={inputRows}
                disabled={isBlocked}
                className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/40 resize-none text-[13px] leading-relaxed transition-colors disabled:opacity-40"
                style={{ minHeight: '46px', maxHeight: '120px' }}
                data-testid="chat-input"
              />
              {!isBlocked && (
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <span className="text-[9px] text-slate-700 font-medium">
                    <CornerDownLeft className="w-3 h-3 inline" /> Wyślij
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading || isBlocked}
              className="h-[46px] w-[46px] rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
              style={{ 
                background: input.trim() && !isLoading && !isBlocked
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'rgba(255,255,255,0.03)',
                boxShadow: input.trim() && !isLoading && !isBlocked ? '0 2px 12px rgba(16,185,129,0.3)' : 'none'
              }}
              data-testid="chat-send-btn"
            >
              {isBlocked && !isPremium ? (
                <Lock className="w-4 h-4 text-slate-500" />
              ) : (
                <Send className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeChatPanel;
